import { useState, useEffect } from 'react';
import { clientsApi } from '../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { ScrollArea } from './ui/scroll-area';
import { UserPlus, Trash2, Users, Clock } from 'lucide-react';
import { toast } from 'sonner';

export default function ClientsDialog({ open, onOpenChange, eventId }) {
  const [clients, setClients] = useState([]);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [inviting, setInviting] = useState(false);

  useEffect(() => {
    if (open && eventId) fetchClients();
  }, [open, eventId]);

  const fetchClients = async () => {
    setLoading(true);
    try {
      const res = await clientsApi.getAll(eventId);
      setClients(res.data);
    } catch {
      toast.error('Error al cargar clientes');
    } finally {
      setLoading(false);
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setInviting(true);
    try {
      const res = await clientsApi.invite(eventId, email.trim());
      setClients(prev => [...prev, res.data]);
      setEmail('');
      toast.success(res.data.status === 'pending'
        ? 'Invitación enviada. El cliente verá el evento cuando cree su cuenta.'
        : 'Cliente invitado correctamente');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al invitar cliente');
    } finally {
      setInviting(false);
    }
  };

  const handleRevoke = async (client) => {
    const label = client.user_name || client.user_email;
    if (!window.confirm(`¿Cancelar acceso a ${label}?`)) return;
    try {
      if (client.status === 'pending') {
        await clientsApi.revokePending(eventId, client.id);
        setClients(prev => prev.filter(c => c.id !== client.id));
      } else {
        await clientsApi.revoke(eventId, client.user_id);
        setClients(prev => prev.filter(c => c.user_id !== client.user_id));
      }
      toast.success('Acceso revocado');
    } catch {
      toast.error('Error al revocar acceso');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Users className="w-5 h-5" />
            Gestión de clientes
          </DialogTitle>
        </DialogHeader>

        <p className="text-sm text-muted-foreground">
          Invita a tu cliente por email. Si aún no tiene cuenta, recibirá la invitación y podrá registrarse para acceder al evento.
        </p>

        {/* Invite form */}
        <form onSubmit={handleInvite} className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="client-email">Email del cliente</Label>
            <div className="flex gap-2">
              <Input
                id="client-email"
                type="email"
                placeholder="cliente@empresa.com"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
              />
              <Button type="submit" disabled={inviting} className="gap-1 flex-shrink-0">
                <UserPlus className="w-4 h-4" />
                {inviting ? 'Invitando...' : 'Invitar'}
              </Button>
            </div>
          </div>
        </form>

        {/* Clients list */}
        <div className="mt-2">
          <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider mb-3">
            Clientes con acceso ({clients.length})
          </p>
          {loading ? (
            <div className="text-sm text-muted-foreground text-center py-4">Cargando...</div>
          ) : clients.length === 0 ? (
            <div className="text-sm text-muted-foreground text-center py-4 border border-dashed border-border rounded-lg">
              Sin clientes invitados todavía
            </div>
          ) : (
            <ScrollArea className="max-h-48">
              <div className="space-y-2">
                {clients.map(client => (
                  <div key={client.id} className="flex items-center justify-between p-3 bg-muted/40 rounded-lg">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium flex-shrink-0 ${client.status === 'pending' ? 'bg-yellow-500/10 text-yellow-500' : 'bg-primary/10'}`}>
                        {client.status === 'pending'
                          ? <Clock className="w-4 h-4" />
                          : (client.user_name?.[0]?.toUpperCase() || '?')}
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5">
                          <p className="text-sm font-medium truncate">
                            {client.user_name || client.user_email}
                          </p>
                          {client.status === 'pending' && (
                            <span className="text-[10px] bg-yellow-500/10 text-yellow-600 dark:text-yellow-400 px-1.5 py-0.5 rounded font-medium flex-shrink-0">
                              Pendiente
                            </span>
                          )}
                        </div>
                        {client.user_name && (
                          <p className="text-xs text-muted-foreground truncate">{client.user_email}</p>
                        )}
                        {client.status === 'pending' && (
                          <p className="text-xs text-muted-foreground">Esperando registro</p>
                        )}
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 flex-shrink-0 text-destructive hover:text-destructive"
                      onClick={() => handleRevoke(client)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </ScrollArea>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
